import { useState, useEffect, useMemo } from "react";
import {
  FACTIONS,
  FACTION_NAMES,
  FACTION_IMAGES,
  getCurrentFactionMonth,
  getFactionLeaderboard,
  switchFaction,
  getFactionHistory,
  getFactionRewardBadge,
  getFactionMembersCount,
  getEffectiveSeatLimit,
  canJoinFaction,
  incrementFactionMembers,
  ensureCurrentMonthMembers,
  correctFactionPoints,
  isFactionPointsActive,
} from "../utils/factionLogic";

const DEV_MOCK_MONTHS = process.env.NODE_ENV === "development" ? [
  { month: "2026-04", data: { bracer: 47, ouroboros: 62, church: 31 } },
  { month: "2026-05", data: { bracer: 88, ouroboros: 55, church: 73 } },
] : null;

function FactionMode({ currentTheme, onOpenLeaderboard }) {
  const showLeaderboardBtn = useMemo(
    () => new URLSearchParams(window.location.search).get("lb") === "1",
    []
  );
  const [faction, setFactionState] = useState(() => localStorage.getItem("kisekidle-faction"));
  const [points, setPoints] = useState(() => parseInt(localStorage.getItem("kisekidle-faction-points") || "0", 10));
  const [leaderboard, setLeaderboard] = useState({ bracer: 0, ouroboros: 0, church: 0 });
  const [history, setHistory] = useState({});
  const [allMonthsData, setAllMonthsData] = useState(null);
  const [allMonthsLoading, setAllMonthsLoading] = useState(false);
  const [confirmSwitch, setConfirmSwitch] = useState(null);
  const [memberCounts, setMemberCounts] = useState(null);
  const [joinError, setJoinError] = useState("");

  const currentMonth = getCurrentFactionMonth();
  const pointsActive = isFactionPointsActive();

  useEffect(() => {
    getFactionLeaderboard(currentMonth).then(setLeaderboard);
    ensureCurrentMonthMembers(currentMonth).then(setMemberCounts);
    setHistory(getFactionHistory());
    correctFactionPoints().then(() => {
      getFactionLeaderboard(currentMonth).then(setLeaderboard);
    });
  }, [currentMonth, faction, points]);

  useEffect(() => {
    if (!faction) return;
    if (allMonthsData) return;

    setAllMonthsLoading(true);
    const months = [];
    const epoch = new Date("2026-04-01T00:00:00Z");
    const now = new Date();
    const d = new Date(epoch);
    while (d <= now) {
      const m = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
      if (m === currentMonth) break;
      months.push(m);
      d.setUTCMonth(d.getUTCMonth() + 1);
    }

    Promise.all(months.map(m => getFactionLeaderboard(m).then(data => ({ month: m, data }))))
      .then((results) => {
        let filtered = results.filter(r => r.data.bracer > 0 || r.data.ouroboros > 0 || r.data.church > 0);
        if (filtered.length === 0 && DEV_MOCK_MONTHS) filtered = DEV_MOCK_MONTHS;
        setAllMonthsData(filtered);
        setAllMonthsLoading(false);
      })
      .catch(() => {
        if (DEV_MOCK_MONTHS) setAllMonthsData(DEV_MOCK_MONTHS);
        setAllMonthsLoading(false);
      });
  }, [faction, allMonthsData]);

  const refresh = () => {
    setFactionState(localStorage.getItem("kisekidle-faction"));
    setPoints(parseInt(localStorage.getItem("kisekidle-faction-points") || "0", 10));
    setHistory(getFactionHistory());
    getFactionLeaderboard(currentMonth).then(setLeaderboard);
    getFactionMembersCount(currentMonth).then(setMemberCounts);
  };

  const handleSelect = async (f) => {
    if (faction === f) return;
    setJoinError("");

    if (!faction) {
      const ok = await canJoinFaction(currentMonth, f);
      if (!ok) {
        const counts = await getFactionMembersCount(currentMonth);
        const limit = getEffectiveSeatLimit(counts);
        setJoinError(`${FACTION_NAMES[f]} is full (${counts[f] || 0}/${limit} members). Pick another faction!`);
        return;
      }
      const hist = JSON.parse(localStorage.getItem("kisekidle-faction-history") || "{}");
      if (!hist[currentMonth]) {
        hist[currentMonth] = f;
        localStorage.setItem("kisekidle-faction-history", JSON.stringify(hist));
      }
      localStorage.setItem("kisekidle-faction", f);
      localStorage.setItem("kisekidle-faction-points", "0");
      await incrementFactionMembers(currentMonth, f);
      refresh();
    } else {
      setConfirmSwitch(f);
    }
  };

  const handleConfirmSwitch = async () => {
    if (!confirmSwitch) return;
    setJoinError("");
    const ok = await switchFaction(confirmSwitch);
    if (ok) {
      setConfirmSwitch(null);
      refresh();
    } else {
      const counts = await getFactionMembersCount(currentMonth);
      const limit = getEffectiveSeatLimit(counts);
      if ((counts[confirmSwitch] || 0) >= limit) {
        setJoinError(`${FACTION_NAMES[confirmSwitch]} is full (${counts[confirmSwitch] || 0}/${limit} members).`);
      } else {
        setJoinError("Not enough points (need 50) or already in this faction.");
      }
    }
  };

  const total = leaderboard.bracer + leaderboard.ouroboros + leaderboard.church;
  const maxVal = Math.max(leaderboard.bracer, leaderboard.ouroboros, leaderboard.church, 1);
  const getWidth = (val) => Math.max(2, (val / maxVal) * 100);

  const winner = maxVal > 0 ? FACTIONS.find(f => leaderboard[f] === maxVal) : null;

  const rulesContent = (
    <div className="faction-rules-grid">
      <div className="faction-rule-item">
        <div className="faction-rule-icon">🤝</div>
        <div className="faction-rule-body">
          <div className="faction-rule-title">Joining a Faction</div>
          <div className="faction-rule-desc">Click any faction card to join on your first pick. You'll start at 0 points. Points go live on June 1st, 2026.</div>
        </div>
      </div>
      <div className="faction-rule-item">
        <div className="faction-rule-icon">⭐</div>
        <div className="faction-rule-body">
          <div className="faction-rule-title">Earning Points</div>
          <div className="faction-rule-desc">Win any daily puzzle → +1 pt. Win on your first guess → +2 pts. Lose → -1 pt (cannot go below 0). Only daily puzzles count — replaying old puzzles from the archive does not earn points. Points reset to 0 on the 1st of each month.</div>
        </div>
      </div>
      <div className="faction-rule-item">
        <div className="faction-rule-icon">🔄</div>
        <div className="faction-rule-body">
          <div className="faction-rule-title">Switching Factions</div>
          <div className="faction-rule-desc">Costs 50 points. Subject to seat limits — you can't switch into a full faction. Your old faction loses one member slot.</div>
        </div>
      </div>
      <div className="faction-rule-item">
        <div className="faction-rule-icon">🪑</div>
        <div className="faction-rule-body">
          <div className="faction-rule-title">Seat Limits</div>
          <div className="faction-rule-desc">No single faction can have more members than ~40% of all active players (at least 20 seats guaranteed). This keeps the competition balanced and prevents one faction from dominating through sheer numbers.</div>
        </div>
      </div>
      <div className="faction-rule-item">
        <div className="faction-rule-icon">🏆</div>
        <div className="faction-rule-body">
          <div className="faction-rule-title">Monthly Leaderboard</div>
          <div className="faction-rule-desc">Resets each month at UTC midnight on the 1st. The faction with the highest total points wins and earns a champion badge.</div>
        </div>
      </div>
      <div className="faction-rule-item">
        <div className="faction-rule-icon">📜</div>
        <div className="faction-rule-body">
          <div className="faction-rule-title">History & Collections</div>
          <div className="faction-rule-desc">Past months are browsable in the leaderboard dropdown. Visit the Central Collection → Factions tab to see every past winner — if you were in the winning faction, the badge unlocks as a collectible.</div>
        </div>
      </div>
    </div>
  );

  return (
    <div className={`profile-container theme-${currentTheme}`}>
      <div className="faction-header">
        <h2 className="mode-title">⚖️ Faction Wars</h2>
        <p className="mode-subtitle">Choose your allegiance and compete for monthly glory</p>
      </div>

      <div className="faction-cards">
        {FACTIONS.map((f) => {
          const selected = faction === f;
          const img = FACTION_IMAGES[f];
          const canAfford = !faction || !pointsActive || points >= 50;
          const count = memberCounts ? memberCounts[f] || 0 : null;
          const limit = memberCounts ? getEffectiveSeatLimit(memberCounts) : null;
          const isFull = count !== null && limit !== null && count >= limit && !selected;
          return (
            <div
              key={f}
              className={`faction-card ${selected ? "selected" : ""} ${!selected && faction && canAfford && !isFull ? "switchable" : ""} ${!selected && faction && !canAfford && !isFull ? "locked" : ""} ${isFull ? "full" : ""}`}
              onClick={() => !isFull && handleSelect(f)}
            >
              <div className="faction-card-glow"></div>
              {img && <img src={img} alt={FACTION_NAMES[f]} className="faction-card-crest" />}
              <h3 className="faction-card-name">{FACTION_NAMES[f]}</h3>
              {memberCounts && (
                <span className="faction-card-members">
                  {memberCounts[f] || 0} / {getEffectiveSeatLimit(memberCounts)}
                </span>
              )}
              {selected && <span className="faction-card-badge">YOUR FACTION</span>}
              {isFull && <span className="faction-card-badge full-badge">FULL</span>}
            </div>
          );
        })}
      </div>



      {joinError && (
        <div className="faction-join-error">{joinError}</div>
      )}

      {confirmSwitch && (
        <div className="faction-switch-confirm">
          <p>Switch to <strong>{FACTION_NAMES[confirmSwitch]}</strong>{pointsActive ? <> for <strong>50 points</strong></> : <> (free during preparation)</>}?</p>
          <div className="faction-switch-actions">
            <button className="submit-button" onClick={handleConfirmSwitch}>Yes, switch</button>
            <button className="submit-button cancel" onClick={() => setConfirmSwitch(null)}>Cancel</button>
          </div>
        </div>
      )}

      {!faction && (
        <div className="faction-empty">
          <p>Click a faction above to join. {pointsActive ? "Earn points by playing daily puzzles!" : "Points start counting June 1st!"}</p>
          <div className="faction-rules">
            <div><strong>Win on 1st try:</strong> +2 pts</div>
            <div><strong>Win:</strong> +1 pt</div>
            <div><strong>Lose:</strong> -1 pt (min 0)</div>
            <div><strong>Archive replays:</strong> no points earned</div>
            <div><strong>Switch faction:</strong> costs 50 pts</div>
            <div><strong>Seat limit:</strong> ~40% of total members max per faction</div>
          </div>
          <details className="faction-rules-panel" style={{ marginTop: 20, textAlign: "left" }}>
            <summary>📖 Full Rules & How It Works</summary>
            {rulesContent}
          </details>
        </div>
      )}

      {faction && (
        <>
          {pointsActive ? (
            <>
              <div className="faction-points-panel">
                <div className="faction-points-label">Your Points</div>
                <div className="faction-points-value">⭐ {points}</div>
                {faction && (
                  <div className="faction-points-note">
                    Switch faction costs 50 pts
                    {points < 50 && <span className="faction-not-enough"> (not enough)</span>}
                  </div>
                )}
              </div>

              <div className="faction-leaderboard-panel">
                <div className="faction-leaderboard-header">
                  <span>🏆 {currentMonth} Leaderboard</span>
                  <span className="faction-total-players">{total} total points</span>
                </div>
                {FACTIONS.map((f) => {
                  const val = leaderboard[f] || 0;
                  const isWinner = f === winner && total > 0;
                  return (
                    <div key={f} className="faction-leaderboard-row">
                      <div className="faction-lb-info">
                        <img src={FACTION_IMAGES[f]} alt={FACTION_NAMES[f]} className="faction-lb-crest" />
                        <span className="faction-lb-name">{FACTION_NAMES[f]}</span>
                        {isWinner && <span className="faction-lb-crown">👑</span>}
                      </div>
                      <div className="faction-lb-bar-track">
                        <div
                          className="faction-lb-bar-fill"
                          style={{ width: `${getWidth(val)}%` }}
                        />
                      </div>
                      <span className="faction-lb-pts">{val} pts</span>
                    </div>
                  );
                })}
              </div>

              {showLeaderboardBtn && (
                <div style={{ textAlign: "center", marginTop: "16px" }}>
                  <button className="submit-button" onClick={onOpenLeaderboard}>
                    🏆 Player Leaderboard
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="faction-prep-message">
              <p>You've joined <strong>{FACTION_NAMES[faction]}</strong>!</p>
              <p>Faction points go live <strong>June 1st, 2026</strong>. You can switch freely until then.</p>
            </div>
          )}

          <div className="faction-history-panel">
            <div className="faction-history-header">🏆 Past Monthly Champions</div>
            {allMonthsLoading ? (
              <p style={{ textAlign: "center", opacity: 0.6 }}>Loading...</p>
            ) : allMonthsData && allMonthsData.length > 0 ? (
              <div className="faction-collection-grid">
                {[...allMonthsData].reverse().map(({ month, data }) => {
                  const pMax = Math.max(data.bracer, data.ouroboros, data.church, 1);
                  const winner = pMax > 0 ? FACTIONS.find(f => data[f] === pMax) : null;
                  return (
                    <div key={month} className="faction-month-card">
                      <div className="faction-month-header">
                        <span className="faction-month-label">{month}</span>
                      </div>
                      {winner ? (
                        <>
                          <div className="faction-month-winner">
                            <img
                              src={FACTION_IMAGES[winner]}
                              alt={FACTION_NAMES[winner]}
                              className="faction-month-crest"
                            />
                            <div className="faction-month-info">
                              <span className="faction-month-winner-name">
                                {FACTION_NAMES[winner]}
                              </span>
                              <span className="faction-month-pts">{data[winner]} pts</span>
                            </div>
                            <img
                              src={getFactionRewardBadge(month, winner)}
                              alt="Champion Badge"
                              className="faction-winner-badge"
                            />
                          </div>
                          <div className="faction-month-breakdown">
                            {FACTIONS.map(f => (
                              <div key={f} className="faction-month-row">
                                <span>{FACTION_NAMES[f]}:</span>
                                <span>{data[f] || 0} pts</span>
                              </div>
                            ))}
                          </div>
                        </>
                      ) : (
                        <p style={{ opacity: 0.5, textAlign: "center" }}>No data</p>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p style={{ textAlign: "center", opacity: 0.6 }}>No past months data yet.</p>
            )}
          </div>

          <details className="faction-rules-panel">
            <summary>📖 Full Rules & How It Works</summary>
            {rulesContent}
          </details>
        </>
      )}
    </div>
  );
}

export default FactionMode;
